import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiError } from "@/api/client";
import { useUsers } from "@/api/queries/users";
import { useUpdateProcess } from "@/api/mutations/processes";

// #303 (UC-2, Arben 2026-08-22 „gesammelt aber nicht vergessen"): den Verantwortlichen
// einer Prozess-Vorlage übergeben.
//
// Die API kann es seit #282 (`PATCH /processes/:id {ownerId}`), die Oberfläche zeigte ihn
// bisher nur als Link — der Wechsel ging also nur per Datenbank oder curl. Muster:
// `SetConnectorOwnerDialog` (#279), mit EINEM Unterschied: hier gibt es kein „Entfernen".
// Eine Vorlage hat IMMER einen Verantwortlichen (der Master verlangt einen `ownerId`-String,
// und ohne Verantwortlichen gäbe es niemanden, der Entwürfe freigibt).

function fehlerText(err: unknown): string {
  const status = err instanceof ApiError ? err.status : 0;
  const code = err instanceof ApiError ? (err.body as { code?: string } | undefined)?.code : undefined;
  if (code === "APPROVAL_FORBIDDEN" || status === 403) return "Keine Berechtigung für diese Vorlage.";
  if (status === 400) return "Diese Person existiert nicht (mehr).";
  return "Übergeben fehlgeschlagen.";
}

export function SetProcessOwnerDialog({
  processId,
  processName,
  currentOwnerId,
  open,
  onOpenChange,
}: {
  processId: string;
  processName: string;
  currentOwnerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const users = useUsers();
  const update = useUpdateProcess();
  const [selected, setSelected] = useState(currentOwnerId);

  // Beim Öffnen auf den Ist-Stand zurück: ein abgebrochener Versuch darf beim nächsten
  // Öffnen nicht als Vorauswahl wieder auftauchen.
  useEffect(() => {
    if (open) setSelected(currentOwnerId);
  }, [open, currentOwnerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-process-owner-dialog">
        <DialogHeader>
          <DialogTitle>Verantwortlichen übergeben</DialogTitle>
          <DialogDescription>
            Wer verantwortet „{processName}" fachlich? Der Verantwortliche darf die Vorlage
            ändern, Versionen freigeben und aktivieren. Eine Vorlage hat immer genau einen
            Verantwortlichen — sie kann nur übergeben, nicht freigestellt werden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="process-owner-select">Person</Label>
          <select
            id="process-owner-select"
            className="h-9 w-full rounded-md border border-border bg-surface px-2 text-[13px]"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="process-owner-select"
          >
            {(users.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
                {u.id === currentOwnerId ? " (aktuell)" : ""}
              </option>
            ))}
          </select>
          {users.isError && (
            <p className="text-[12px] text-danger">Die Personen-Liste konnte nicht geladen werden.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() =>
              update.mutate(
                { id: processId, patch: { ownerId: selected } },
                {
                  onSuccess: () => {
                    toast.success("Verantwortlicher übergeben");
                    onOpenChange(false);
                  },
                  onError: (err) => toast.error(fehlerText(err)),
                },
              )
            }
            disabled={update.isPending || !selected || selected === currentOwnerId}
            data-testid="process-owner-save"
          >
            Übergeben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
